import { SubscriptionPlan, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import {
  NUMBER_OF_LOCATIONS_LIMIT,
  MAX_DEPTH_FOR_NESTED_DEPARMENTS_CORE,
  NUMBER_OF_TEAMS_LIMIT,
  FREEMIUM_LEAVE_TYPES_LIMIT,
  DEFAULT_WORKING_HOURS_IN_DAY,
} from '@vacationtracker/shared/data/app-parameters'
import { buildDepartmentTree, getMaxDepth } from '@vacationtracker/shared/functions/team-tree'
import { ApproversLevelEnum } from '@vacationtracker/shared/types/team'
import { ICompanyWorkWeek, IGetLocationsWithLeavePoliciesShort } from '../types/locations'
import { IGetCompanyBillingTeamList } from '../types/teams'
import { IGetLeaveTypeListLimits } from '../types/company'

export interface IPlanDowngradeSourceData {
  plan: SubscriptionPlan | null
  teams: IGetCompanyBillingTeamList[]
  locations: IGetLocationsWithLeavePoliciesShort[]
  leaveTypes: IGetLeaveTypeListLimits[]
  workWeeks: ICompanyWorkWeek[]
}

export interface IFreemiumRestrictions {
  disableTeamsLimit: boolean
  disableLocationsLimit: boolean
  disableSubdepartmentsLimit: boolean
  disableMultiLevelApproval: boolean
  disableNotificationWatchers: boolean
  disableLeaveTypesLimit: boolean
  totalSubdepartments: number
  disableCustomWorkWeek: boolean
}

const DEFAULT_RESTRICTIONS: IFreemiumRestrictions = {
  disableTeamsLimit: false,
  disableLocationsLimit: false,
  disableSubdepartmentsLimit: false,
  disableMultiLevelApproval: false,
  disableNotificationWatchers: false,
  disableLeaveTypesLimit: false,
  totalSubdepartments: 0,
  disableCustomWorkWeek: false,
}

/**
 * Pure function to calculate plan downgrade restrictions
 *
 * Returns restrictions that determine if a company can downgrade to a lower plan tier.
 * Handles both Complete→Core and Complete/Core→Freemium downgrades.
 * If any restriction is true, the company must first reduce their usage before downgrading.
 *
 * @param sourceData - Company data including plan, teams, locations, leave types, and work weeks
 * @returns Object containing all restriction flags and metadata
 */
export function calculateFreemiumRestrictions(sourceData: IPlanDowngradeSourceData | null): IFreemiumRestrictions {
  if (!sourceData) {
    return DEFAULT_RESTRICTIONS
  }

  const { plan, teams, locations, leaveTypes, workWeeks } = sourceData

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const teamTree = buildDepartmentTree(teams as any)
  const teamDepth = getMaxDepth(teamTree)
  const isCompletePlan = plan === SubscriptionPlanEnum.complete
  const activeLeaveTypes = leaveTypes.filter((leaveType) => leaveType.isActive)

  return {
    disableTeamsLimit: isCompletePlan && teams.length > NUMBER_OF_TEAMS_LIMIT,
    disableLocationsLimit: isCompletePlan && locations.length > NUMBER_OF_LOCATIONS_LIMIT,
    disableSubdepartmentsLimit: isCompletePlan && teamDepth > MAX_DEPTH_FOR_NESTED_DEPARMENTS_CORE,
    disableMultiLevelApproval: isCompletePlan && teams.some((team) => team.approvers?.some((approver) => approver?.approverLevels?.includes(ApproversLevelEnum.SECOND))),
    disableNotificationWatchers: isCompletePlan && teams.some((team) => team.watchersToNotify && team.watchersToNotify.length > 0),
    disableLeaveTypesLimit: isCompletePlan && activeLeaveTypes.length > FREEMIUM_LEAVE_TYPES_LIMIT,
    totalSubdepartments: teamDepth,
    disableCustomWorkWeek:
      isCompletePlan && workWeeks.some((workWeek) => workWeek.workHours?.some((workDay) => Number(workDay[1]) !== DEFAULT_WORKING_HOURS_IN_DAY) || workWeek.schedule),
  }
}
